import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useMe } from '../auth/MeProvider';
import type { Device, DeviceStatus, MonthToDateCost, Network } from '../api/types';
import { liveDeviceIds } from './status';
import { errorText, useRelay, type UseRelay } from './useRelay';

/**
 * Data the shell shows on every screen (sidebar counts, the Networks list, the status bar)
 * and the pages reuse, so a screen never fetches the relay or the device list a second time.
 *
 * Each list is loaded once and again only through its reloader (a page's Refresh, or after a
 * mutation): every call wakes the scale-to-zero API, so nothing here polls.
 */
export interface ShellData {
  relay: UseRelay;
  devices: Device[] | null;
  devicesError: string | null;
  reloadDevices: () => Promise<void>;
  /** GET /devices/status, one per device of `devices`; null until loaded. */
  statuses: DeviceStatus[] | null;
  statusesError: string | null;
  reloadStatuses: () => Promise<void>;
  /**
   * Device ids at either end of a live flow (GET /flows, admin-only): the cyan dots. Empty for a
   * non-admin, until loaded, or when the read failed; a missing halo is not worth an error banner.
   */
  live: ReadonlySet<string>;
  reloadLive: () => Promise<void>;
  /** Month-to-date cost; null for non-admins, until loaded, or when Azure failed (see costError). */
  cost: MonthToDateCost | null;
  costError: string | null;
  reloadCost: () => Promise<void>;
  /** GET /networks, by CIDR; null for non-admins (derive them from `devices`) or until loaded. */
  networks: Network[] | null;
  /** What a new network would take; null when none is left or not loaded. */
  nextFreeCidr: string | null;
  networksError: string | null;
  reloadNetworks: () => Promise<void>;
  /** Unpublished draft changes (GET /policy/diff); null for non-admins or until loaded. */
  changeCount: number | null;
  reloadDiff: () => Promise<void>;
}

const ShellContext = createContext<ShellData | null>(null);

/** GET /flows is newest first, so the live flows are among the first page. */
const LIVE_FLOW_LIMIT = 100;
const NO_LIVE: ReadonlySet<string> = new Set();

export function ShellProvider({ children }: { children: ReactNode }) {
  const { api } = useAuth();
  const { canUseVpn, isAdmin } = useMe();
  const relay = useRelay(api, canUseVpn);
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [devicesError, setDevicesError] = useState<string | null>(null);
  const [statuses, setStatuses] = useState<DeviceStatus[] | null>(null);
  const [statusesError, setStatusesError] = useState<string | null>(null);
  const [cost, setCost] = useState<MonthToDateCost | null>(null);
  const [costError, setCostError] = useState<string | null>(null);
  const [networks, setNetworks] = useState<Network[] | null>(null);
  const [nextFreeCidr, setNextFreeCidr] = useState<string | null>(null);
  const [networksError, setNetworksError] = useState<string | null>(null);
  const [changeCount, setChangeCount] = useState<number | null>(null);
  const [live, setLive] = useState<ReadonlySet<string>>(NO_LIVE);

  const reloadDevices = useCallback(async () => {
    try {
      setDevices(await api.listDevices());
      setDevicesError(null);
    } catch (e) {
      setDevicesError(errorText(e));
    }
  }, [api]);

  const reloadStatuses = useCallback(async () => {
    try {
      setStatuses(await api.deviceStatuses());
      setStatusesError(null);
    } catch (e) {
      setStatusesError(errorText(e));
    }
  }, [api]);

  const reloadLive = useCallback(async () => {
    try {
      setLive(liveDeviceIds((await api.listFlows({ limit: LIVE_FLOW_LIMIT })).flows));
    } catch {
      setLive(NO_LIVE);
    }
  }, [api]);

  const reloadCost = useCallback(async () => {
    try {
      setCost(await api.costMonthToDate());
      setCostError(null);
    } catch (e) {
      // 502 upstream_error: Azure failed and nothing is cached for this month.
      setCost(null);
      setCostError(errorText(e));
    }
  }, [api]);

  const reloadNetworks = useCallback(async () => {
    try {
      const list = await api.listNetworks();
      setNetworks(list.networks);
      setNextFreeCidr(list.nextFreeCidr);
      setNetworksError(null);
    } catch (e) {
      setNetworksError(errorText(e));
    }
  }, [api]);

  const reloadDiff = useCallback(async () => {
    try {
      setChangeCount((await api.getDiff()).changeCount);
    } catch {
      // Only a badge: without it the nav item still works, and the Firewall page shows the error.
      setChangeCount(null);
    }
  }, [api]);

  useEffect(() => {
    if (canUseVpn) {
      void reloadDevices();
      void reloadStatuses();
    }
  }, [canUseVpn, reloadDevices, reloadStatuses]);

  // The diff, the cost, the networks and the flows are admin-only; a non-admin would only collect 403s.
  useEffect(() => {
    if (isAdmin) {
      void reloadDiff();
      void reloadCost();
      void reloadNetworks();
      void reloadLive();
    } else {
      setChangeCount(null);
      setCost(null);
      setNetworks(null);
      setNextFreeCidr(null);
      setLive(NO_LIVE);
    }
  }, [isAdmin, reloadDiff, reloadCost, reloadNetworks, reloadLive]);

  const value = useMemo<ShellData>(
    () => ({
      relay,
      devices,
      devicesError,
      reloadDevices,
      statuses,
      statusesError,
      reloadStatuses,
      live,
      reloadLive,
      cost,
      costError,
      reloadCost,
      networks,
      nextFreeCidr,
      networksError,
      reloadNetworks,
      changeCount,
      reloadDiff,
    }),
    [
      relay, devices, devicesError, reloadDevices, statuses, statusesError, reloadStatuses, live, reloadLive, cost, costError,
      reloadCost, networks, nextFreeCidr, networksError, reloadNetworks, changeCount, reloadDiff,
    ],
  );
  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
}

export function useShell(): ShellData {
  const ctx = useContext(ShellContext);
  if (!ctx) throw new Error('useShell must be used inside <ShellProvider>');
  return ctx;
}
